import { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import { useVeloraStore } from '../store/useVeloraStore';
import {
  searchDeezer, getSavedSongs, saveSong, removeSavedSong, isSongSaved,
  type DeezerTrack,
} from '../lib/songSearch';

type Difficulty = 'easy' | 'medium' | 'hard';
type Category = 'Scales' | 'Arpeggios' | 'Exercises';

interface PracticeTrack {
  id: string; name: string; key: string;
  difficulty: Difficulty; category: Category; durationSec: number;
}

const PRACTICE_TRACKS: PracticeTrack[] = [
  { id: 'scale-c', name: 'C Major Scale', key: 'C', difficulty: 'easy', category: 'Scales', durationSec: 30 },
  { id: 'scale-g', name: 'G Major Scale', key: 'G', difficulty: 'easy', category: 'Scales', durationSec: 30 },
  { id: 'arp-c', name: 'C Major Arpeggio', key: 'C', difficulty: 'easy', category: 'Arpeggios', durationSec: 25 },
  { id: 'arp-am', name: 'A Minor Arpeggio', key: 'Am', difficulty: 'medium', category: 'Arpeggios', durationSec: 25 },
  { id: 'fifth-c', name: 'Perfect Fifth Jumps', key: 'C', difficulty: 'medium', category: 'Exercises', durationSec: 40 },
  { id: 'octave-c', name: 'Octave Slides', key: 'C', difficulty: 'hard', category: 'Exercises', durationSec: 35 },
];

const DIFFICULTY_COLORS: Record<Difficulty, string> = { easy: '#4ade80', medium: '#facc15', hard: '#f87171' };
const CATEGORIES: Category[] = ['Scales', 'Arpeggios', 'Exercises'];
const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

function useSpeechSearch(onResult: (text: string) => void) {
  const [listening, setListening] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recRef = useRef<any>(null);
  const toggle = () => {
    if (!SR) return;
    if (listening) { recRef.current?.stop(); return; }
    const r = new SR();
    r.lang = 'en-US'; r.interimResults = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    r.onresult = (e: any) => onResult(e.results[0]?.[0]?.transcript ?? '');
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recRef.current = r; r.start(); setListening(true);
  };
  return { listening, toggle };
}

export default function Songs() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { favoriteSongs, toggleFavorite } = useVeloraStore();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<Category | 'All'>('All');
  const { listening, toggle: toggleMic } = useSpeechSearch(text => setSearch(text));

  const [deezerTracks, setDeezerTracks] = useState<DeezerTrack[]>([]);
  const [deezerLoading, setDeezerLoading] = useState(false);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [savedSongs, setSavedSongs] = useState<DeezerTrack[]>(() => getSavedSongs());
  const [savedIds, setSavedIds] = useState<Set<number>>(() => new Set(getSavedSongs().map(s => s.id)));

  useEffect(() => {
    if (search.length < 2) { setDeezerTracks([]); return; }
    const t = setTimeout(async () => {
      setDeezerLoading(true);
      const results = await searchDeezer(search);
      setDeezerTracks(results); setDeezerLoading(false);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { return () => { previewAudioRef.current?.pause(); }; }, []);

  const handlePreview = (track: DeezerTrack) => {
    if (!track.previewUrl) return;
    if (!previewAudioRef.current) previewAudioRef.current = new Audio();
    const audio = previewAudioRef.current;
    if (previewId === track.id) { audio.pause(); setPreviewId(null); }
    else {
      audio.pause(); audio.src = track.previewUrl;
      audio.play().catch(() => {}); setPreviewId(track.id);
      audio.onended = () => setPreviewId(null);
    }
  };

  const goKaraoke = (track: DeezerTrack) => {
    previewAudioRef.current?.pause(); setPreviewId(null);
    navigate('/song', { state: { mode: 'karaoke', artist: track.artist, title: track.title } });
  };

  const handleSave = useCallback((track: DeezerTrack) => {
    if (isSongSaved(track.id)) {
      removeSavedSong(track.id);
      setSavedIds(prev => { const s = new Set(prev); s.delete(track.id); return s; });
      setSavedSongs(getSavedSongs());
    } else {
      saveSong(track);
      setSavedIds(prev => new Set(prev).add(track.id));
      setSavedSongs(getSavedSongs());
    }
  }, []);

  const filtered = PRACTICE_TRACKS.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase());
    const matchesTab = activeTab === 'All' || t.category === activeTab;
    return matchesSearch && matchesTab;
  });

  const favorites = PRACTICE_TRACKS.filter(t => favoriteSongs.includes(t.id));
  const showDeezer = search.length >= 2;

  return (
    <div className="flex flex-col min-h-screen pb-24" style={{ background: 'transparent' }}>

      {/* Header */}
      <div className="safe-top px-5 pt-4 pb-4 fade-up">
        <p className="text-xs tracking-widest uppercase font-medium mb-1"
          style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Library</p>
        <h1 className="text-2xl font-bold mb-3" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>SONGS</h1>

        {/* Search bar */}
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 glass-card rounded-2xl px-3 py-2.5">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke={theme.muted} strokeWidth="1.8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke={theme.muted} strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search songs or artists…"
              className="flex-1 bg-transparent text-sm outline-none"
              style={{ color: theme.text, fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ color: theme.muted, fontSize: 14 }}>✕</button>
            )}
          </div>
          {SR && (
            <button
              onClick={toggleMic}
              className="glass-card rounded-2xl px-3 transition-all active:scale-90"
              style={{
                background: listening ? 'rgba(248,113,113,0.15)' : undefined,
                borderColor: listening ? 'rgba(248,113,113,0.35)' : undefined,
                color: listening ? '#f87171' : theme.muted,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect x="9" y="2" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="1.8"/>
                <path d="M5 10a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 px-4">

        {/* Deezer Search results */}
        {showDeezer && (
          <div className="fade-up-1">
            <p className="text-xs font-semibold tracking-widest uppercase mb-2"
              style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>
              Search Results
            </p>
            {deezerLoading ? (
              <div className="glass-card rounded-2xl p-4 text-center">
                <div className="breathe text-sm" style={{ color: theme.muted }}>Searching…</div>
              </div>
            ) : deezerTracks.length === 0 ? (
              <div className="glass-card rounded-2xl p-4 text-center">
                <p className="text-sm" style={{ color: theme.muted }}>No results found</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {deezerTracks.map(track => (
                  <DeezerCard key={track.id} track={track}
                    isPreviewing={previewId === track.id}
                    isSaved={savedIds.has(track.id)}
                    onPreview={() => handlePreview(track)}
                    onSave={() => handleSave(track)}
                    onKaraoke={() => goKaraoke(track)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Saved songs */}
        {savedSongs.length > 0 && (
          <div className="fade-up-2">
            <p className="text-xs font-semibold tracking-widest uppercase mb-2"
              style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Saved Songs</p>
            <div className="flex flex-col gap-2">
              {savedSongs.map(track => (
                <DeezerCard key={track.id} track={track}
                  isPreviewing={previewId === track.id}
                  isSaved={true}
                  onPreview={() => handlePreview(track)}
                  onSave={() => handleSave(track)}
                  onKaraoke={() => goKaraoke(track)} />
              ))}
            </div>
          </div>
        )}

        {/* Favorites */}
        {favorites.length > 0 && (
          <div className="fade-up-2">
            <p className="text-xs font-semibold tracking-widest uppercase mb-2"
              style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Favourites</p>
            <div className="flex flex-col gap-2">
              {favorites.map(track => (
                <TrackCard key={track.id} track={track}
                  isFavorite={favoriteSongs.includes(track.id)}
                  onFavorite={() => toggleFavorite(track.id)}
                  onPlay={() => navigate('/song', { state: { trackName: track.name, trackId: track.id } })} />
              ))}
            </div>
          </div>
        )}

        {/* Practice Tracks */}
        <div className="fade-up-3">
          <p className="text-xs font-semibold tracking-widest uppercase mb-2"
            style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Practice Tracks</p>
          <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
            {(['All', ...CATEGORIES] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className="px-3 py-1.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all glass-card"
                style={{
                  background: activeTab === tab ? 'rgba(83,74,183,0.28)' : undefined,
                  borderColor: activeTab === tab ? 'rgba(127,119,221,0.45)' : undefined,
                  color: activeTab === tab ? theme.text : theme.muted,
                  fontFamily: 'Syne, sans-serif',
                }}>
                {tab}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            {filtered.length === 0 ? (
              <div className="glass-card rounded-2xl p-6 text-center">
                <p className="text-sm" style={{ color: theme.muted }}>No tracks found</p>
              </div>
            ) : (
              filtered.map(track => (
                <TrackCard key={track.id} track={track}
                  isFavorite={favoriteSongs.includes(track.id)}
                  onFavorite={() => toggleFavorite(track.id)}
                  onPlay={() => navigate('/song', { state: { trackName: track.name, trackId: track.id } })} />
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

function DeezerCard({ track, isPreviewing, isSaved, onPreview, onSave, onKaraoke }: {
  track: DeezerTrack; isPreviewing: boolean; isSaved: boolean;
  onPreview: () => void; onSave: () => void; onKaraoke: () => void;
}) {
  return (
    <div className="glass-card rounded-2xl flex items-center gap-3 px-3 py-2.5">
      {track.albumCover ? (
        <img src={track.albumCover} alt="" className="w-10 h-10 rounded-xl shrink-0 object-cover"
          style={{ border: '1px solid rgba(255,255,255,0.10)' }} />
      ) : (
        <div className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center"
          style={{ background: 'rgba(83,74,183,0.18)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M9 18V5l12-2v13" stroke="#7F77DD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="6" cy="18" r="3" stroke="#7F77DD" strokeWidth="1.8"/>
          </svg>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: '#EEEDFE' }}>{track.title}</p>
        <p className="text-xs truncate mt-0.5" style={{ color: 'rgba(238,237,254,0.45)' }}>{track.artist}</p>
      </div>
      <button onClick={onSave} className="glass-btn w-8 h-8 flex items-center justify-center"
        style={{ borderRadius: 10, borderColor: isSaved ? 'rgba(155,148,232,0.4)' : undefined }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill={isSaved ? '#9B94E8' : 'none'}
          stroke={isSaved ? '#9B94E8' : 'rgba(238,237,254,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
        </svg>
      </button>
      <button onClick={onPreview} className="glass-btn w-8 h-8 flex items-center justify-center"
        style={{ borderRadius: 10, borderColor: isPreviewing ? 'rgba(127,119,221,0.45)' : undefined }}>
        {isPreviewing
          ? <svg width="12" height="12" viewBox="0 0 24 24" fill="#7F77DD"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
          : <svg width="12" height="12" viewBox="0 0 24 24" fill="rgba(238,237,254,0.6)"><polygon points="5,3 19,12 5,21"/></svg>
        }
      </button>
      <button onClick={onKaraoke} className="glass-btn glass-btn-accent w-8 h-8 flex items-center justify-center"
        style={{ borderRadius: 10 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect x="9" y="2" width="6" height="12" rx="3" stroke="white" strokeWidth="1.8"/>
          <path d="M5 10a7 7 0 0 0 14 0" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
          <line x1="12" y1="17" x2="12" y2="21" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}

function TrackCard({ track, isFavorite, onFavorite, onPlay }: {
  track: PracticeTrack; isFavorite: boolean; onFavorite: () => void; onPlay: () => void;
}) {
  return (
    <div className="glass-card rounded-2xl flex items-center gap-3 px-4 py-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: 'rgba(83,74,183,0.18)' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M9 18V5l12-2v13" stroke="#7F77DD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="6" cy="18" r="3" stroke="#7F77DD" strokeWidth="1.8"/>
          <circle cx="18" cy="16" r="3" stroke="#7F77DD" strokeWidth="1.8"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: '#EEEDFE' }}>{track.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs font-mono" style={{ color: 'rgba(238,237,254,0.45)' }}>{track.key}</span>
          <span className="text-xs" style={{ color: 'rgba(238,237,254,0.2)' }}>·</span>
          <span className="text-xs" style={{ color: 'rgba(238,237,254,0.45)' }}>{fmt(track.durationSec)}</span>
          <span className="text-xs px-1.5 py-0.5 rounded-lg font-semibold"
            style={{ background: `${DIFFICULTY_COLORS[track.difficulty]}15`,
              color: DIFFICULTY_COLORS[track.difficulty],
              border: `1px solid ${DIFFICULTY_COLORS[track.difficulty]}28` }}>
            {track.difficulty}
          </span>
        </div>
      </div>
      <button onClick={onFavorite} className="p-2 transition-all active:scale-90"
        style={{ color: isFavorite ? '#f472b6' : 'rgba(238,237,254,0.3)', fontSize: 16 }}>
        {isFavorite ? '♥' : '♡'}
      </button>
      <button onClick={onPlay} className="glass-btn glass-btn-accent w-9 h-9 flex items-center justify-center"
        style={{ borderRadius: 12 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
          <polygon points="5,3 19,12 5,21"/>
        </svg>
      </button>
    </div>
  );
}
